import { useState } from "react";
import {
    Camera,
    ChevronDown,
    ChevronUp,
    Dice5,
    Package,
    Palette,
    Plus,
    Share2,
    Spline,
    Trash2,
} from "lucide-react";
import { useVolumeBuilder3d } from "./VolumeBuilder3dContext";
import { useUndoRedoKeyboard } from "../../hooks/useUndoRedoKeyboard";
import {
    FILL_RESOLUTION_MAX,
    FILL_RESOLUTION_MIN,
    RADIAL_SEGMENTS_MAX,
    RADIAL_SEGMENTS_MIN,
} from "../../utils/volumeBuilderGeometry";
import type {
    FillObjectType,
    Path3D,
    VolumeShape,
} from "../../utils/volumeBuilderGeometry";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { CheckboxField } from "../reusable/CheckboxField";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { ColorPaletteEditor } from "../reusable/ColorPaletteEditor";
import { PresetSelector } from "../reusable/PresetSelector";
import { SendSvgToToolMenu } from "../reusable/SendSvgToToolMenu";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";
import { UndoRedoControls } from "../reusable/UndoRedoControls";

const SHAPE_OPTIONS: Array<{ value: VolumeShape; label: string }> = [
    { value: "cuboid", label: "Cuboid" },
    { value: "cylinder", label: "Cylinder" },
    { value: "sphere", label: "Sphere" },
    { value: "billboard", label: "Billboard" },
];

const FILL_TYPE_OPTIONS: Array<{ value: FillObjectType; label: string }> = [
    { value: "cuboid", label: "Cubes" },
    { value: "cylinder", label: "Cylinders" },
    { value: "sphere", label: "Spheres" },
    { value: "billboard", label: "Billboards" },
];

function toggleType(
    types: FillObjectType[],
    type: FillObjectType,
): FillObjectType[] {
    if (types.includes(type)) {
        const next = types.filter((t) => t !== type);
        // Keep at least one type selected.
        return next.length > 0 ? next : types;
    }
    return [...types, type];
}

function num(v: string | number, fallback: number): number {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : fallback;
}

interface PathRowProps {
    path: Path3D;
    index: number;
    total: number;
}

function PathRow({ path, index, total }: PathRowProps) {
    const {
        updatePath,
        removePath,
        reorderPath,
        addPoint,
        removePoint,
        updatePoint,
        pushPathsSnapshot,
    } = useVolumeBuilder3d();
    const [open, setOpen] = useState(index === 0);

    const usesRadial = path.shape === "cylinder" || path.shape === "sphere";

    return (
        <li className="volume-path-row" style={rowStyle}>
            <div style={rowHeaderStyle}>
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => setOpen((v) => !v)}
                    aria-expanded={open}
                    title={open ? "Collapse" : "Expand"}
                    style={{ padding: "2px 6px" }}
                >
                    {open ? (
                        <ChevronUp size={16} aria-hidden />
                    ) : (
                        <ChevronDown size={16} aria-hidden />
                    )}
                </button>
                <input
                    type="text"
                    value={path.name}
                    onChange={(e) => updatePath(path.id, { name: e.target.value })}
                    aria-label={`Path ${index + 1} name`}
                    style={{ flex: 1, minWidth: 0 }}
                />
                <span className="path-tool-meta" style={{ opacity: 0.7 }}>
                    {path.shape}
                </span>
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => reorderPath(index, index - 1)}
                    disabled={index === 0}
                    title="Move up"
                    style={{ padding: "2px 6px" }}
                >
                    <ChevronUp size={16} aria-hidden />
                </button>
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => reorderPath(index, index + 1)}
                    disabled={index === total - 1}
                    title="Move down"
                    style={{ padding: "2px 6px" }}
                >
                    <ChevronDown size={16} aria-hidden />
                </button>
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => removePath(path.id)}
                    disabled={total <= 1}
                    title="Remove path"
                    style={{ padding: "2px 6px" }}
                >
                    <Trash2 size={16} aria-hidden />
                </button>
            </div>

            {open && (
                <div style={{ marginTop: 8 }}>
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            Shape
                            <select
                                className="anchor-select"
                                value={path.shape}
                                onChange={(e) =>
                                    updatePath(path.id, {
                                        shape: e.target.value as VolumeShape,
                                    })
                                }
                            >
                                {SHAPE_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="field-label">
                            Cross size
                            <DebouncedInput
                                type="number"
                                min={0.01}
                                step={0.05}
                                value={path.crossSize}
                                onChange={(v) =>
                                    updatePath(path.id, {
                                        crossSize: Math.max(0.01, num(v, 0.5)),
                                    })
                                }
                            />
                        </label>
                    </div>

                    <div className="field-row field-row--two">
                        <label className="field-label">
                            Color
                            <input
                                type="color"
                                value={path.color}
                                onChange={(e) =>
                                    updatePath(path.id, { color: e.target.value })
                                }
                            />
                        </label>
                        {usesRadial && (
                            <label className="field-label">
                                Segments
                                <DebouncedInput
                                    type="number"
                                    min={RADIAL_SEGMENTS_MIN}
                                    max={RADIAL_SEGMENTS_MAX}
                                    step={1}
                                    value={path.radialSegments}
                                    onChange={(v) =>
                                        updatePath(path.id, {
                                            radialSegments: Math.max(
                                                RADIAL_SEGMENTS_MIN,
                                                Math.min(
                                                    RADIAL_SEGMENTS_MAX,
                                                    Math.round(num(v, 16)),
                                                ),
                                            ),
                                        })
                                    }
                                />
                            </label>
                        )}
                    </div>

                    <CheckboxField
                        checked={path.visible}
                        onChange={(checked) =>
                            updatePath(path.id, { visible: checked })
                        }
                        label="Visible"
                        inline
                    />

                    {/* Container fill */}
                    {path.shape !== "billboard" && (
                        <>
                            <p
                                className="path-tool-meta"
                                style={sectionLabelStyle}
                            >
                                Fill (pack objects inside the volume)
                            </p>
                            <CheckboxField
                                checked={path.fill.enabled}
                                onChange={(checked) =>
                                    updatePath(path.id, {
                                        fill: {
                                            ...path.fill,
                                            enabled: checked,
                                        },
                                    })
                                }
                                label="Fill container with objects"
                                title="The path's shape becomes an invisible container; only the packed objects render."
                                inline
                            />
                            {path.fill.enabled && (
                                <>
                                    <div className="field-row field-row--two">
                                        <label className="field-label">
                                            Resolution
                                            <DebouncedInput
                                                type="number"
                                                min={FILL_RESOLUTION_MIN}
                                                max={FILL_RESOLUTION_MAX}
                                                step={1}
                                                value={path.fill.resolution}
                                                onChange={(v) =>
                                                    updatePath(path.id, {
                                                        fill: {
                                                            ...path.fill,
                                                            resolution: Math.max(
                                                                FILL_RESOLUTION_MIN,
                                                                Math.min(
                                                                    FILL_RESOLUTION_MAX,
                                                                    Math.round(
                                                                        num(
                                                                            v,
                                                                            5,
                                                                        ),
                                                                    ),
                                                                ),
                                                            ),
                                                        },
                                                    })
                                                }
                                            />
                                        </label>
                                        <label className="field-label">
                                            Object scale
                                            <DebouncedInput
                                                type="number"
                                                min={0.05}
                                                max={1}
                                                step={0.05}
                                                value={path.fill.objectScale}
                                                onChange={(v) =>
                                                    updatePath(path.id, {
                                                        fill: {
                                                            ...path.fill,
                                                            objectScale: Math.max(
                                                                0.05,
                                                                Math.min(
                                                                    1,
                                                                    num(v, 0.8),
                                                                ),
                                                            ),
                                                        },
                                                    })
                                                }
                                            />
                                        </label>
                                    </div>
                                    <p
                                        className="path-tool-meta"
                                        style={{ margin: "4px 0" }}
                                    >
                                        Object types
                                    </p>
                                    <div
                                        style={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: 8,
                                        }}
                                    >
                                        {FILL_TYPE_OPTIONS.map((t) => {
                                            const checked =
                                                path.fill.objectTypes.includes(
                                                    t.value,
                                                );
                                            return (
                                                <CheckboxField
                                                    key={t.value}
                                                    checked={checked}
                                                    onChange={() =>
                                                        updatePath(path.id, {
                                                            fill: {
                                                                ...path.fill,
                                                                objectTypes:
                                                                    toggleType(
                                                                        path.fill
                                                                            .objectTypes,
                                                                        t.value,
                                                                    ),
                                                            },
                                                        })
                                                    }
                                                    label={t.label}
                                                    inline
                                                />
                                            );
                                        })}
                                    </div>
                                    <label className="field-label">
                                        Fill seed
                                        <div style={{ display: "flex", gap: 4 }}>
                                            <DebouncedInput
                                                type="number"
                                                step={1}
                                                value={path.fill.seed}
                                                onChange={(v) =>
                                                    updatePath(path.id, {
                                                        fill: {
                                                            ...path.fill,
                                                            seed: Math.round(
                                                                num(v, 1),
                                                            ),
                                                        },
                                                    })
                                                }
                                            />
                                            <button
                                                type="button"
                                                className="generic-button"
                                                title="Randomize fill seed"
                                                style={{ padding: "2px 6px" }}
                                                onClick={() =>
                                                    updatePath(path.id, {
                                                        fill: {
                                                            ...path.fill,
                                                            seed: Math.floor(
                                                                Math.random() *
                                                                    100000,
                                                            ),
                                                        },
                                                    })
                                                }
                                            >
                                                <Dice5 size={16} aria-hidden />
                                            </button>
                                        </div>
                                    </label>
                                </>
                            )}
                        </>
                    )}

                    {/* Packing */}
                    <p className="path-tool-meta" style={sectionLabelStyle}>
                        Packing
                    </p>
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            Mode
                            <select
                                className="anchor-select"
                                value={path.packing.mode}
                                onChange={(e) =>
                                    updatePath(path.id, {
                                        packing: {
                                            ...path.packing,
                                            mode: e.target
                                                .value as Path3D["packing"]["mode"],
                                        },
                                    })
                                }
                            >
                                <option value="free">Free (authored)</option>
                                <option value="bin-pack">Bin-pack</option>
                            </select>
                        </label>
                        <label className="field-label">
                            Count
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={200}
                                step={1}
                                value={path.packing.count}
                                onChange={(v) =>
                                    updatePath(path.id, {
                                        packing: {
                                            ...path.packing,
                                            count: Math.max(
                                                1,
                                                Math.min(
                                                    200,
                                                    Math.round(num(v, 1)),
                                                ),
                                            ),
                                        },
                                    })
                                }
                            />
                        </label>
                    </div>
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            Margin
                            <DebouncedInput
                                type="number"
                                min={0}
                                step={0.05}
                                value={path.packing.margin}
                                onChange={(v) =>
                                    updatePath(path.id, {
                                        packing: {
                                            ...path.packing,
                                            margin: Math.max(0, num(v, 0.1)),
                                        },
                                    })
                                }
                            />
                        </label>
                        <label className="field-label">
                            Seed
                            <div style={{ display: "flex", gap: 4 }}>
                                <DebouncedInput
                                    type="number"
                                    step={1}
                                    value={path.packing.seed}
                                    onChange={(v) =>
                                        updatePath(path.id, {
                                            packing: {
                                                ...path.packing,
                                                seed: Math.round(num(v, 1)),
                                            },
                                        })
                                    }
                                />
                                <button
                                    type="button"
                                    className="generic-button"
                                    title="Randomize seed"
                                    style={{ padding: "2px 6px" }}
                                    onClick={() =>
                                        updatePath(path.id, {
                                            packing: {
                                                ...path.packing,
                                                seed: Math.floor(
                                                    Math.random() * 100000,
                                                ),
                                            },
                                        })
                                    }
                                >
                                    <Dice5 size={16} aria-hidden />
                                </button>
                            </div>
                        </label>
                    </div>

                    {/* Points */}
                    <p className="path-tool-meta" style={sectionLabelStyle}>
                        {path.shape === "billboard"
                            ? "Points (billboard uses their midpoint)"
                            : "Points (spine: top → bottom)"}
                    </p>
                    <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                        {path.points.map((pt, i) => (
                            <li
                                key={i}
                                className="field-row"
                                style={{ gap: 4, alignItems: "center" }}
                            >
                                {(["x", "y", "z"] as const).map((axis) => (
                                    <DebouncedInput
                                        key={axis}
                                        type="number"
                                        step={0.1}
                                        aria-label={`Point ${i + 1} ${axis}`}
                                        title={axis.toUpperCase()}
                                        value={pt[axis]}
                                        onChange={(v) => {
                                            pushPathsSnapshot();
                                            updatePoint(path.id, i, {
                                                ...pt,
                                                [axis]: num(v, pt[axis]),
                                            });
                                        }}
                                        style={{ width: 0, flex: 1, minWidth: 0 }}
                                    />
                                ))}
                                <button
                                    type="button"
                                    className="generic-button"
                                    onClick={() => removePoint(path.id, i)}
                                    disabled={path.points.length <= 2}
                                    title="Remove point"
                                    style={{ padding: "2px 6px" }}
                                >
                                    <Trash2 size={14} aria-hidden />
                                </button>
                            </li>
                        ))}
                    </ul>
                    <button
                        type="button"
                        className="generic-button"
                        onClick={() => addPoint(path.id)}
                        style={{ marginTop: 6 }}
                    >
                        <Plus size={16} aria-hidden /> Add point
                    </button>
                </div>
            )}
        </li>
    );
}

const rowStyle: React.CSSProperties = {
    border: "1px solid var(--control-border, #3a3a4a)",
    borderRadius: 6,
    padding: 8,
    marginBottom: 8,
    listStyle: "none",
};
const rowHeaderStyle: React.CSSProperties = {
    display: "flex",
    gap: 4,
    alignItems: "center",
};
const sectionLabelStyle: React.CSSProperties = {
    margin: "10px 0 4px",
    fontWeight: 600,
    opacity: 0.85,
};

export function VolumeBuilder3dControls() {
    const {
        paths,
        addPath,
        undo,
        redo,
        canUndo,
        canRedo,
        cameraParams,
        setCameraParams,
        packingBounds,
        setPackingBounds,
        palette,
        setPalette,
        ambient,
        setAmbient,
        backfaceCulling,
        setBackfaceCulling,
        faceStrokeEnabled,
        setFaceStrokeEnabled,
        faceStrokeColor,
        setFaceStrokeColor,
        faceStrokeWidth,
        setFaceStrokeWidth,
        viewBoxWidth,
        setViewBoxWidth,
        viewBoxHeight,
        setViewBoxHeight,
        canExport,
        buildExportSvgString,
        applyPreset,
        getPresetValues,
        resetParameters,
    } = useVolumeBuilder3d();

    useUndoRedoKeyboard({ undo, redo });

    return (
        <div className="panel-controls path-populate-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Volume Builder 3D</h2>
                    <ToolDescriptionPopover dialogLabel="Volume Builder 3D tool description">
                        <p>
                            Define polylines in 3D space and extrude each into a
                            primitive volume (cuboid, cylinder, sphere) swept
                            along its spine, or a camera-facing billboard. Pack
                            copies in 3D, orbit the live preview, then project
                            the scene into a depth-sorted, shaded SVG.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <UndoRedoControls
                        canUndo={canUndo}
                        canRedo={canRedo}
                        onUndo={undo}
                        onRedo={redo}
                    />
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                </div>
            </div>

            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="volume-builder-3d"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />

                <CollapsibleToolGroup title="Paths" icon={Spline}>
                    <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                        {paths.map((path, i) => (
                            <PathRow
                                key={path.id}
                                path={path}
                                index={i}
                                total={paths.length}
                            />
                        ))}
                    </ul>
                    <button
                        type="button"
                        className="generic-button"
                        onClick={addPath}
                    >
                        <Plus size={16} aria-hidden /> Add path
                    </button>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Camera" icon={Camera}>
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            Projection
                            <select
                                className="anchor-select"
                                value={cameraParams.projectionType}
                                onChange={(e) =>
                                    setCameraParams({
                                        projectionType: e.target
                                            .value as VolumeCameraProjection,
                                    })
                                }
                            >
                                <option value="perspective">Perspective</option>
                                <option value="orthographic">Orthographic</option>
                            </select>
                        </label>
                        <label className="field-label">
                            FOV
                            <DebouncedInput
                                type="number"
                                min={5}
                                max={170}
                                step={1}
                                value={cameraParams.fov}
                                onChange={(v) =>
                                    setCameraParams({
                                        fov: Math.max(
                                            5,
                                            Math.min(170, num(v, 45)),
                                        ),
                                    })
                                }
                            />
                        </label>
                    </div>
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            Distance
                            <DebouncedInput
                                type="number"
                                min={0.5}
                                max={100}
                                step={0.5}
                                value={cameraParams.distance}
                                onChange={(v) =>
                                    setCameraParams({
                                        distance: Math.max(0.5, num(v, 7)),
                                    })
                                }
                            />
                        </label>
                        <span />
                    </div>
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            Output width
                            <DebouncedInput
                                type="number"
                                min={50}
                                max={4000}
                                step={10}
                                value={viewBoxWidth}
                                onChange={(v) =>
                                    setViewBoxWidth(
                                        Math.max(50, Math.round(num(v, 600))),
                                    )
                                }
                            />
                        </label>
                        <label className="field-label">
                            Output height
                            <DebouncedInput
                                type="number"
                                min={50}
                                max={4000}
                                step={10}
                                value={viewBoxHeight}
                                onChange={(v) =>
                                    setViewBoxHeight(
                                        Math.max(50, Math.round(num(v, 600))),
                                    )
                                }
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Packing bounds" icon={Package}>
                    <p className="path-tool-meta" style={{ margin: "0 0 6px" }}>
                        Bin-packed copies are placed inside this volume.
                    </p>
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            Bounds X
                            <DebouncedInput
                                type="number"
                                min={0.5}
                                step={0.5}
                                value={packingBounds.x}
                                onChange={(v) =>
                                    setPackingBounds({
                                        x: Math.max(0.5, num(v, 6)),
                                    })
                                }
                            />
                        </label>
                        <label className="field-label">
                            Bounds Y
                            <DebouncedInput
                                type="number"
                                min={0.5}
                                step={0.5}
                                value={packingBounds.y}
                                onChange={(v) =>
                                    setPackingBounds({
                                        y: Math.max(0.5, num(v, 6)),
                                    })
                                }
                            />
                        </label>
                    </div>
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            Bounds Z
                            <DebouncedInput
                                type="number"
                                min={0.5}
                                step={0.5}
                                value={packingBounds.z}
                                onChange={(v) =>
                                    setPackingBounds({
                                        z: Math.max(0.5, num(v, 6)),
                                    })
                                }
                            />
                        </label>
                        <span />
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Appearance" icon={Palette}>
                    <ColorPaletteEditor
                        colors={palette}
                        onChange={setPalette}
                        description="Shading ramp (dark → light) tints each face by orientation."
                        minColors={1}
                        maxColors={8}
                    />
                    <label className="field-label">
                        Ambient ({ambient.toFixed(2)})
                        <input
                            type="range"
                            min={0}
                            max={1}
                            step={0.05}
                            value={ambient}
                            onChange={(e) => setAmbient(Number(e.target.value))}
                        />
                    </label>
                    <CheckboxField
                        checked={backfaceCulling}
                        onChange={setBackfaceCulling}
                        label="Backface culling"
                        inline
                    />
                    <CheckboxField
                        checked={faceStrokeEnabled}
                        onChange={setFaceStrokeEnabled}
                        label="Face stroke"
                        inline
                    />
                    {faceStrokeEnabled && (
                        <div className="field-row field-row--two">
                            <label className="field-label">
                                Stroke color
                                <input
                                    type="color"
                                    value={faceStrokeColor}
                                    onChange={(e) =>
                                        setFaceStrokeColor(e.target.value)
                                    }
                                />
                            </label>
                            <label className="field-label">
                                Stroke width
                                <DebouncedInput
                                    type="number"
                                    min={0}
                                    step={0.1}
                                    value={faceStrokeWidth}
                                    onChange={(v) =>
                                        setFaceStrokeWidth(
                                            Math.max(0, num(v, 0.5)),
                                        )
                                    }
                                />
                            </label>
                        </div>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Send to tool" icon={Share2}>
                    <SendSvgToToolMenu
                        disabled={!canExport}
                        getSvg={() => ({
                            name: "volume-builder-3d-export.svg",
                            content: buildExportSvgString(),
                        })}
                        allowedSlots={["path", "object", "primary"]}
                        excludeTargetPanels={["volume-builder-3d"]}
                    />
                </CollapsibleToolGroup>
            </div>
        </div>
    );
}

type VolumeCameraProjection = "perspective" | "orthographic";
